import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Home as HomeIcon, Heart as HeartIcon, ShoppingBag as ShoppingBagIcon, Truck as TruckIcon, ArrowLeft, Loader, Mail, Phone, MapPin, MessageSquare } from 'lucide-react';
import { useCart } from '../context/CartContext';
import { API_URL } from '../config';
import TopBar from '../components/TopBar';
import Header from '../components/Header';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import './Contact.css';

const InstagramIcon = ({ size = 20 }) => (
  <svg 
    xmlns="http://www.w3.org/2000/svg" 
    width={size} 
    height={size} 
    viewBox="0 0 24 24" 
    fill="none" 
    stroke="currentColor" 
    strokeWidth="2" 
    strokeLinecap="round" 
    strokeLinejoin="round"
  >
    <rect x="2" y="2" width="20" height="20" rx="5" ry="5"></rect>
    <path d="M16 11.37A4 4 0 1 1 12.63 8 4 4 0 0 1 16 11.37z"></path>
    <line x1="17.5" y1="6.5" x2="17.51" y2="6.5"></line>
  </svg>
);

const FacebookIcon = ({ size = 20 }) => (
  <svg 
    xmlns="http://www.w3.org/2000/svg" 
    width={size} 
    height={size} 
    viewBox="0 0 24 24" 
    fill="none" 
    stroke="currentColor" 
    strokeWidth="2" 
    strokeLinecap="round" 
    strokeLinejoin="round"
  >
    <path d="M18 2h-3a5 5 0 0 0-5 5v3H7v4h3v8h4v-8h3l1-4h-4V7a1 1 0 0 1 1-1h3z"></path>
  </svg>
);


const defaultContact = {
  email: "support@himalayannuts.com",
  phone: "+91 98765 43210",
  address: "Pristine Valleys, Himachal Pradesh, India",
  whatsapp: "https://wa.me/919876543210",
  instagram: "https://instagram.com/himalayannuts",
  facebook: "https://facebook.com/himalayannuts"
};

const Contact = () => {
  const navigate = useNavigate();
  const { wishlistCount, cartCount } = useCart();
  const [contactData, setContactData] = useState(defaultContact);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    setLoading(true);
    fetch(`${API_URL}/settings/contact_settings`)
      .then((res) => {
        if (!res.ok) throw new Error('Failed to load contact settings');
        return res.json();
      })
      .then((data) => {
        if (data && data.email) {
          setContactData(data);
        }
      })
      .catch((err) => console.error('Error fetching contact settings:', err))
      .finally(() => setLoading(false));
  }, []);

  return (
    <div className="contact-page-wrapper">
      {/* Top Announcement Bar */}
      <TopBar />

      {/* Main Header */}
      <Header 
        wishlistCount={wishlistCount} 
        cartCount={cartCount} 
        onCartClick={() => navigate('/cart')} 
      />

      {/* Navigation Bar */}
      <Navbar activeItem="Contact Us" />

      {/* Main Content */}
      <main className="contact-main">
        <div className="container">
          <div className="contact-header-row">
            <Link to="/" className="back-btn">
              <ArrowLeft size={16} />
              <span>Back to Shop</span>
            </Link>
            <h1 className="contact-page-title">Contact Us</h1>
          </div>

          {loading ? (
            <div className="contact-loader-container">
              <Loader className="spinner" size={40} />
              <p>Loading contact details...</p>
            </div>
          ) : (
            <div className="contact-content-grid">
              {/* Left Column: Direct Contacts */}
              <div className="contact-info-col">
                <h2 className="contact-subtitle">Get in Touch</h2>
                <p className="contact-desc">
                  Have questions about our sourcing, packaging, or bulk orders? Reach out to us through any of the channels below.
                </p>

                <div className="contact-methods">
                  {/* Phone */}
                  <div className="contact-method-card">
                    <div className="method-icon-box">
                      <Phone size={22} />
                    </div>
                    <div className="method-details">
                      <h3>Call Us</h3>
                      <a href={`tel:${contactData.phone}`} className="method-link">
                        {contactData.phone}
                      </a>
                      <p>Mon - Sat, 9 AM - 6 PM IST</p>
                    </div>
                  </div>

                  {/* Email */}
                  <div className="contact-method-card">
                    <div className="method-icon-box">
                      <Mail size={22} />
                    </div>
                    <div className="method-details">
                      <h3>Email Support</h3>
                      <a href={`mailto:${contactData.email}`} className="method-link">
                        {contactData.email}
                      </a>
                      <p>Expect a response within 24 hours.</p>
                    </div>
                  </div>

                  {/* Address */}
                  <div className="contact-method-card">
                    <div className="method-icon-box">
                      <MapPin size={22} />
                    </div>
                    <div className="method-details">
                      <h3>Office Address</h3>
                      <p className="method-address-text">{contactData.address}</p>
                      <p>Sourced and processed in small batches.</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Right Column: Social Media / Chat Actions */}
              <div className="contact-social-col">
                <h2 className="contact-subtitle">Chat & Social Links</h2>
                <p className="contact-desc">
                  Connect with us instantly on WhatsApp for order support, or follow our social channels for updates and healthy living tips.
                </p>

                <div className="social-action-buttons">
                  {/* WhatsApp */}
                  {contactData.whatsapp && (
                    <a 
                      href={contactData.whatsapp} 
                      target="_blank" 
                      rel="noopener noreferrer" 
                      className="social-btn whatsapp-btn"
                    >
                      <MessageSquare size={20} />
                      <span>Message on WhatsApp</span>
                    </a>
                  )}

                  {/* Instagram */}
                  {contactData.instagram && (
                    <a 
                      href={contactData.instagram} 
                      target="_blank" 
                      rel="noopener noreferrer" 
                      className="social-btn instagram-btn"
                    >
                      <InstagramIcon size={20} />
                      <span>Follow on Instagram</span>
                    </a>
                  )}

                  {/* Facebook */}
                  {contactData.facebook && (
                    <a 
                      href={contactData.facebook} 
                      target="_blank" 
                      rel="noopener noreferrer" 
                      className="social-btn facebook-btn"
                    >
                      <FacebookIcon size={20} />
                      <span>Like on Facebook</span>
                    </a>
                  )}
                </div>

                <div className="secure-badge-box">
                  <div className="secure-indicator">
                    <div className="dot"></div>
                    <span>Fully Verified Support channels</span>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </main>

      {/* Footer */}
      <Footer />

      {/* Sticky Bottom Navigation (Mobile screens only) */}
      <div className="mobile-sticky-nav" role="navigation" aria-label="Mobile Navigation">
        <Link 
          to="/" 
          className="mobile-nav-item"
        >
          <HomeIcon size={20} />
          <span>Home</span>
        </Link>
        <Link 
          to="/track" 
          className="mobile-nav-item"
        >
          <TruckIcon size={20} />
          <span>Track Order</span>
        </Link>
        <Link 
          to="/wishlist" 
          className="mobile-nav-item"
        >
          <HeartIcon size={20} />
          {wishlistCount > 0 && (
            <span className="badge-count">{wishlistCount}</span>
          )}
          <span>Wishlist</span>
        </Link>
        <Link 
          to="/cart" 
          className="mobile-nav-item"
        >
          <ShoppingBagIcon size={20} />
          {cartCount > 0 && (
            <span className="badge-count">{cartCount}</span>
          )}
          <span>Cart</span>
        </Link>
      </div>
    </div>
  );
};

export default Contact;
